import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { sectionClientTotal, sectionTrueCost, detectType, itemTrueCost } from '../lib/estimateFinancials'

// ─── Customer-Safe Proposal Preview ────────────────────────────────────────────
// Admin-side preview of what a customer will see once a proposal is sent — NOT the
// client portal (that's separate, later work blocked on H2 per Docs/ROADMAP.md).
//
// CUSTOMER-SAFE ONLY. This component must never render: unit costs, itemTrueCost,
// margin, markup, labor rates, or internal notes. Only aggregate section totals via
// sectionClientTotal() — the same customer-facing helper used by the estimate PDF —
// are used for any dollar figure derived from line items. See Docs/QUOTE_TO_CASH.md
// "Proposal Rules" for the full should-show / should-not-show list this was audited
// against.
//
// Data note: proposals.sections_data is a frozen JSON snapshot taken at generation
// time (see EstimateBuilder.generateProposal) — it intentionally does NOT live-read
// the source estimate, so edits to the estimate after this proposal was generated
// will not change what's rendered here. That's the versioning model from
// Docs/DECISIONS_NEEDED.md ("Proposal V1 Data Model").

const PROPOSAL_STATUS_COLOR = { draft: '#6b7280', sent: '#2563eb', viewed: '#7c3aed', approved: '#16a34a', declined: '#dc2626', expired: '#6b7280', superseded: '#9ca3af' }
const PROPOSAL_STATUS_BG    = { draft: '#f3f4f6', sent: '#dbeafe', viewed: '#ede9fe', approved: '#dcfce7', declined: '#fef2f2', expired: '#f3f4f6', superseded: '#f3f4f6' }

const OPTION_STATUS_LABEL = { offered: 'Offered · Not Accepted', accepted: 'Accepted', declined: 'Declined', expired: 'Expired / Removed' }
const OPTION_STATUS_COLOR = { offered: '#1d4ed8', accepted: '#16a34a', declined: '#dc2626', expired: '#6b7280' }
const OPTION_STATUS_BG    = { offered: '#eff6ff', accepted: '#f0fdf4', declined: '#fef2f2', expired: '#f3f4f6' }

const DEFAULT_PAYMENT_TERMS = 'Deposit is due prior to commencement of work. Final payment is due upon completion. Additional payments above the deposit are welcome at any time.'

export default function ProposalPreview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [proposal, setProposal] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [updatingOptionId, setUpdatingOptionId] = useState(null)
  const [optionError, setOptionError] = useState(null)
  const [approving, setApproving] = useState(false)
  const [approvalSource, setApprovalSource] = useState('manual')
  const [existingJob, setExistingJob] = useState(null)

  useEffect(() => { fetchProposal() }, [id])

  async function fetchProposal() {
    setLoading(true)
    setError(null)
    const [proposalRes, optionsRes] = await Promise.all([
      supabase.from('proposals').select('*, client:clients(name, address, phone, email)').eq('id', id).single(),
      supabase.from('proposal_options').select('*').eq('proposal_id', id),
    ])
    if (proposalRes.error || !proposalRes.data) {
      setError(proposalRes.error?.message || 'Proposal not found.')
      setLoading(false)
      return
    }
    setProposal(proposalRes.data)
    setOptions(optionsRes.data || [])
    // Known already, up front, not just discovered on click -- this is what lets the
    // header show "View Job" instead of "Convert to Job" when one already exists.
    const { data: jobData } = await supabase.from('jobs').select('id, title').eq('proposal_id', id).maybeSingle()
    setExistingJob(jobData || null)
    setLoading(false)
  }

  // Session 3.3 — Optional Add-On Acceptance Model. No client portal yet (blocked on
  // H2), so this is admin-recorded: John marks what the customer told him (text/phone/
  // call/in person), same manual-approval pattern as whole-proposal approval in
  // Docs/QUOTE_TO_CASH.md. Only offered/accepted options were ever meant to affect the
  // contract total (see the ProposalPreview optionalAddOnsTotal comment below, and
  // EstimateBuilder.generateProposal's "nothing accepted yet on a fresh proposal") --
  // this is what actually makes that true instead of aspirational.
  //
  // Reuses the exact formula from EstimateBuilder.generateProposal rather than
  // inventing new financial logic: selected_contract_total = base_scope_total + sum of
  // accepted option prices; tax_amount and deposit_amount are re-derived from that new
  // total using the proposal's existing tax_rate/deposit_pct, unchanged since
  // generation. Once Session 3.4 (Approval Lock) exists, this action should be
  // disabled for locked/approved proposals -- no lock exists yet, so nothing to check.
  async function handleOptionStatusChange(optionId, newStatus) {
    // Session 3.4 lock — approved/declined truth must not silently mutate. The UI
    // already hides these buttons in that state; this is the belt-and-suspenders check.
    if (proposal.status === 'approved' || proposal.status === 'declined') return

    setUpdatingOptionId(optionId)
    setOptionError(null)

    const { error: optErr } = await supabase.from('proposal_options').update({ status: newStatus }).eq('id', optionId)
    if (optErr) {
      setOptionError('Failed to update option: ' + optErr.message)
      setUpdatingOptionId(null)
      return
    }

    const nextOptions = options.map(o => o.id === optionId ? { ...o, status: newStatus } : o)
    const acceptedTotal = nextOptions.filter(o => o.status === 'accepted').reduce((sum, o) => sum + (o.price || 0), 0)
    const newSelectedContractTotal = (proposal.base_scope_total || 0) + acceptedTotal
    const newTaxAmount = newSelectedContractTotal * (proposal.tax_rate || 0)
    const newDepositAmount = (newSelectedContractTotal + newTaxAmount) * ((proposal.deposit_pct || 0) / 100)

    const { error: propErr } = await supabase.from('proposals').update({
      selected_contract_total: newSelectedContractTotal,
      tax_amount: newTaxAmount,
      deposit_amount: newDepositAmount,
    }).eq('id', proposal.id)

    if (propErr) {
      setOptionError('Option was updated but the contract total failed to recalculate: ' + propErr.message)
      setUpdatingOptionId(null)
      return
    }

    setOptions(nextOptions)
    setProposal(p => ({ ...p, selected_contract_total: newSelectedContractTotal, tax_amount: newTaxAmount, deposit_amount: newDepositAmount }))
    setUpdatingOptionId(null)
  }

  // Session 3.4 — Proposal Approval Lock. `approval_source` is constrained by the DB
  // (proposals_status_check / the column's CHECK) to 'portal' | 'manual' | 'email' —
  // portal isn't an option yet since there's no client-facing flow (H2). No dedicated
  // notes/note-text column exists on `proposals` for capturing *how* exactly ("text on
  // June 28" per QUOTE_TO_CASH.md's Manual Approval Notes example) — that's a real gap,
  // filed as Docs/TECH_DEBT.md TD-017 rather than worked around by stuffing it into an
  // unrelated field.
  async function handleApprove(source) {
    setApproving(true)
    setOptionError(null)
    const approvedContractAmount = (proposal.selected_contract_total || 0) + (proposal.tax_amount || 0)
    const { error: approveErr } = await supabase.from('proposals').update({
      status: 'approved',
      approved_at: new Date().toISOString(),
      approved_contract_amount: approvedContractAmount,
      approval_source: source,
    }).eq('id', proposal.id)
    setApproving(false)
    if (approveErr) { setOptionError('Failed to mark approved: ' + approveErr.message); return }
    setProposal(p => ({ ...p, status: 'approved', approved_at: new Date().toISOString(), approved_contract_amount: approvedContractAmount, approval_source: source }))
  }

  async function handleDecline(source) {
    setApproving(true)
    setOptionError(null)
    const { error: declineErr } = await supabase.from('proposals').update({
      status: 'declined',
      declined_at: new Date().toISOString(),
      approval_source: source,
    }).eq('id', proposal.id)
    setApproving(false)
    if (declineErr) { setOptionError('Failed to mark declined: ' + declineErr.message); return }
    setProposal(p => ({ ...p, status: 'declined', declined_at: new Date().toISOString(), approval_source: source }))
  }

  // Session 4.1 — Convert Approved Proposal to Job. This is the real fix, not just a
  // new button: the OLD conversion path (EstimateBuilder.convertToJob) builds jobs
  // straight from the live estimate and completely bypasses proposals/approval --
  // found during this session's audit of "how does this actually fit into job flow."
  // This path sources every number from the approved proposal instead.
  //
  // Frozen baseline correctness: only base sections + ACCEPTED optional sections count
  // toward planned costs. QUOTE_TO_CASH.md is explicit that a declined/unaccepted
  // add-on "must not become part of... Frozen baseline" -- the old path predates
  // accept/decline existing at all (Session 3.3) and summed every section regardless,
  // which is exactly the class of bug this whole proposal/approval layer exists to
  // prevent. Not fixing that path here -- out of scope, it's superseded, not patched.
  async function convertToJob() {
    if (proposal.status !== 'approved') {
      const proceed = window.confirm(
        `This proposal is "${proposal.status}", not approved. Converting before approval means the contract amount isn't locked/verified yet. Continue anyway?`
      )
      if (!proceed) return
    }

    // Re-check live rather than trust the on-load state alone -- avoids a race if a
    // job was created from this proposal in another tab/session since this page loaded.
    const { data: liveExistingJob } = await supabase.from('jobs').select('id, title').eq('proposal_id', proposal.id).maybeSingle()
    if (liveExistingJob) {
      const proceed = window.confirm(`This proposal was already converted to job "${liveExistingJob.title}". Convert again and create a second job?`)
      if (!proceed) return
    }

    const acceptedSectionRefs = new Set(options.filter(o => o.status === 'accepted').map(o => o.section_ref))
    const includedSections = sections.filter(sec => !sec.is_overhead && (!sec.client_optional || acceptedSectionRefs.has(sec._id)))
    const overheadCostIncluded = sections.filter(sec => sec.is_overhead).reduce((sum, sec) => sum + sectionTrueCost(sec), 0)

    const costByType = { Material: 0, Labor: 0, Equipment: 0, Other: 0 }
    let laborHoursIncluded = 0
    for (const sec of includedSections) {
      for (const item of sec.items || []) {
        const type = detectType(item, sec.name)
        costByType[type] = (costByType[type] || 0) + itemTrueCost(item)
        if (type === 'Labor') laborHoursIncluded += parseFloat(item.qty || 0)
      }
    }
    const loadedLaborCost = costByType.Labor + overheadCostIncluded
    // True cost via sectionTrueCost sum, not costByType breakdown -- detectType() can
    // return 'Overhead' for an item whose section name happens to contain "overhead"
    // even when the section isn't is_overhead-flagged, which costByType's four fixed
    // keys wouldn't catch. sectionTrueCost sums every item regardless of category, so
    // this stays correct even in that edge case; costByType is only used for the
    // labor/material/equipment breakdown fields below, not the total.
    const plannedTotalCost = includedSections.reduce((sum, sec) => sum + sectionTrueCost(sec), 0) + overheadCostIncluded
    // Use the locked approved amount when it exists; falls back to the current selected
    // total only via the not-approved warn-and-continue path above.
    const plannedRevenue = proposal.approved_contract_amount != null ? proposal.approved_contract_amount : selectedContractTotalWithTax
    const plannedGrossProfit = plannedRevenue - plannedTotalCost
    const plannedMargin = plannedRevenue > 0 ? (plannedGrossProfit / plannedRevenue) * 100 : 0

    const params = new URLSearchParams()
    if (proposal.client_id) params.set('client', proposal.client_id)
    if (proposal.estimate_id) params.set('estimate', proposal.estimate_id)
    params.set('proposal', proposal.id)
    params.set('estimate_price', plannedRevenue.toFixed(2))
    params.set('planned_material_cost', costByType.Material.toFixed(2))
    params.set('planned_labor_cost', loadedLaborCost.toFixed(2))
    params.set('planned_labor_hours', laborHoursIncluded.toFixed(2))
    params.set('planned_overhead_cost', overheadCostIncluded.toFixed(2))
    params.set('planned_total_cost', plannedTotalCost.toFixed(2))
    params.set('planned_revenue', plannedRevenue.toFixed(2))
    params.set('planned_gross_profit', plannedGrossProfit.toFixed(2))
    params.set('planned_margin', plannedMargin.toFixed(2))
    if (description) params.set('scope_text', encodeURIComponent(description))
    navigate(`/jobs/new?${params.toString()}`)
  }

  if (loading) return <div style={s.loading}>Loading proposal...</div>
  if (error || !proposal) {
    return (
      <div style={s.loading}>
        Could not load this proposal{error ? `: ${error}` : '.'}<br />
        <Link to="/estimates" style={s.backLink}>← Back to Estimates</Link>
      </div>
    )
  }

  // Parse the frozen snapshot — never re-read the live estimate.
  let sections = [], description = null, clientNotes = null
  try {
    const parsed = JSON.parse(proposal.sections_data || '{}')
    sections = parsed.sections || []
    description = parsed.description || null
    clientNotes = parsed.clientNotes || null
  } catch (e) {
    // leave sections empty — surfaced below as "no scope data"
  }
  let visibility = {}
  try { visibility = JSON.parse(proposal.visibility_settings || '{}') } catch (e) {}

  // Recompute overhead spread from the snapshot the same way the PDF does, so section
  // totals here match what was quoted — overhead is baked into labor pricing and is
  // never shown as its own line. Overhead sections themselves are never rendered.
  const overheadTotal = sections.filter(sec => sec.is_overhead).reduce((sum, sec) => sum + sectionTrueCost(sec), 0)
  let totalLaborHours = 0
  for (const sec of sections) {
    if (sec.is_overhead) continue
    for (const item of sec.items || []) {
      if (detectType(item, sec.name) === 'Labor') totalLaborHours += parseFloat(item.qty || 0)
    }
  }
  const overheadPerLaborHour = totalLaborHours > 0 ? overheadTotal / totalLaborHours : 0

  const baseSections = sections.filter(sec => !sec.is_overhead && !sec.client_optional)
  const optionalSections = sections.filter(sec => !sec.is_overhead && sec.client_optional)

  const client = proposal.client
  const selectedContractTotalWithTax = (proposal.selected_contract_total || 0) + (proposal.tax_amount || 0)
  // Only offered/accepted options count as "available" — a declined or expired
  // add-on's price must not inflate this total. This is distinct from
  // selectedContractTotalWithTax above, which reads proposal.selected_contract_total
  // directly from the DB and only ever includes accepted options (see
  // handleOptionStatusChange) — that field is the actual billable truth, this one is
  // just "what's still on the table."
  const optionalAddOnsTotal = options
    .filter(o => o.status !== 'declined' && o.status !== 'expired')
    .reduce((sum, o) => sum + (o.price || 0), 0)
  const isLocked = proposal.status === 'approved' || proposal.status === 'declined'

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          {/* Goes back to wherever you actually came from (job history, proposals
              list, estimate) instead of always assuming the source estimate --
              was a real bug when opened from a Job's Documents & History tab. */}
          <button onClick={() => navigate(-1)} style={{ ...s.backLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Back</button>
          <div style={s.titleRow}>
            <h1 style={s.title}>Proposal {proposal.proposal_number}</h1>
            <span style={{ ...s.statusBadge, background: PROPOSAL_STATUS_BG[proposal.status], color: PROPOSAL_STATUS_COLOR[proposal.status] }}>{proposal.status}</span>
          </div>
          <p style={s.sub}>Admin preview of the customer-facing proposal — not the client portal view</p>
        </div>
        {existingJob ? (
          <Link to={`/jobs/${existingJob.id}`} style={{ ...s.convertBtn, textDecoration: 'none', display: 'inline-block', background: '#0369a1' }}>→ View Job: {existingJob.title}</Link>
        ) : proposal.status !== 'declined' && (
          <button style={s.convertBtn} onClick={convertToJob}>→ Convert to Job</button>
        )}
      </div>

      <div style={s.cautionBanner}>
        <span style={{ fontSize: '16px' }}>⚠️</span>
        <div>
          <strong>Internal preview only.</strong> This screen has not been through a security/data-exposure review yet.
          Do not send this proposal to a real client until that review is complete — see Docs/DECISIONS_NEEDED.md.
        </div>
      </div>

      {/* Proposal Document */}
      <div style={s.doc}>
        <div style={s.docHeader}>
          <div>
            <div style={s.proposalTag}>PROPOSAL</div>
            <div style={s.companyName}>TLC Unlimited</div>
            <div style={s.companyInfo}>Landscaping & Tree Service · El Paso, TX</div>
          </div>
          <div style={s.docHeaderRight}>
            <div style={s.proposalNumLarge}>{proposal.proposal_number}</div>
            <div style={s.proposalDate}>Date: {proposal.created_at ? new Date(proposal.created_at).toLocaleDateString() : '—'}</div>
            {visibility.show_expiry_date !== false && proposal.expires_at && (
              <div style={s.proposalExpiry}>Valid until: {new Date(proposal.expires_at + 'T12:00:00').toLocaleDateString()}</div>
            )}
          </div>
        </div>

        {client && (
          <div style={s.billTo}>
            <div style={s.billToLabel}>Prepared For</div>
            <div style={s.billToName}>{client.name}</div>
            {client.address && <div style={s.billToInfo}>{client.address}</div>}
            {client.phone && <div style={s.billToInfo}>{client.phone}</div>}
            {client.email && <div style={s.billToInfo}>{client.email}</div>}
          </div>
        )}

        {visibility.show_description !== false && description && (
          <div style={s.scopeBlock}>
            <div style={s.blockLabel}>Scope of Work</div>
            <div style={s.blockText}>{description}</div>
          </div>
        )}

        {/* Base scope sections — section totals only, never itemized line costs */}
        <div style={s.sectionsBlock}>
          <div style={s.blockLabel}>Scope Sections</div>
          {baseSections.length === 0 && <div style={s.emptyNote}>No scope sections in this proposal.</div>}
          {baseSections.map(sec => (
            <div key={sec._id} style={s.sectionRow}>
              <div style={{ flex: 1 }}>
                <div style={s.sectionName}>{sec.name}</div>
                {visibility.show_description !== false && sec.section_description && <div style={s.sectionDesc}>{sec.section_description}</div>}
              </div>
              <div style={s.sectionTotal}>${sectionClientTotal(sec, overheadPerLaborHour).toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Optional add-ons — shown separately, clearly not included unless accepted */}
        {optionalSections.length > 0 && (
          <div style={s.optionsBlock}>
            <div style={{ ...s.blockLabel, color: '#1d4ed8' }}>Optional Add-Ons — Not Included Unless Accepted</div>
            {optionError && <div style={s.optionErrorBox}>{optionError}</div>}
            {optionalSections.map(sec => {
              const opt = options.find(o => o.section_ref === sec._id)
              const optStatus = opt?.status || 'offered'
              const price = opt?.price ?? sectionClientTotal(sec, overheadPerLaborHour)
              const busy = opt && updatingOptionId === opt.id
              return (
                <div key={sec._id} style={s.sectionRow}>
                  <div style={{ flex: 1 }}>
                    <div style={s.sectionName}>{sec.name}</div>
                    {visibility.show_description !== false && sec.section_description && <div style={s.sectionDesc}>{sec.section_description}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ ...s.optionBadge, background: OPTION_STATUS_BG[optStatus], color: OPTION_STATUS_COLOR[optStatus] }}>{OPTION_STATUS_LABEL[optStatus]}</span>
                    <div style={{ ...s.sectionTotal, color: '#1d4ed8' }}>+${price.toFixed(2)}</div>
                    {opt && !isLocked && (
                      <div style={s.optionActions}>
                        {optStatus !== 'accepted' && (
                          <button style={s.optionAcceptBtn} disabled={busy} onClick={() => handleOptionStatusChange(opt.id, 'accepted')}>
                            {busy ? '...' : 'Accept'}
                          </button>
                        )}
                        {optStatus !== 'declined' && (
                          <button style={s.optionDeclineBtn} disabled={busy} onClick={() => handleOptionStatusChange(opt.id, 'declined')}>
                            {busy ? '...' : 'Decline'}
                          </button>
                        )}
                        {optStatus !== 'offered' && (
                          <button style={s.optionResetBtn} disabled={busy} onClick={() => handleOptionStatusChange(opt.id, 'offered')}>
                            Reset
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Tax summary + selected contract total */}
        <div style={s.totalsWrap}>
          <div style={s.totalsBox}>
            <div style={s.totalsRow}><span style={s.totalsLabel}>Base Scope Total</span><span style={s.totalsVal}>${(proposal.base_scope_total || 0).toFixed(2)}</span></div>
            {optionalSections.length > 0 && (
              <div style={s.totalsRow}><span style={{ ...s.totalsLabel, color: '#1d4ed8' }}>Optional Add-Ons Available</span><span style={{ ...s.totalsVal, color: '#1d4ed8' }}>+${optionalAddOnsTotal.toFixed(2)}</span></div>
            )}
            {proposal.tax_rate > 0 && (
              <div style={s.totalsRow}><span style={s.totalsLabel}>Tax ({(proposal.tax_rate * 100).toFixed(2)}%)</span><span style={s.totalsVal}>${(proposal.tax_amount || 0).toFixed(2)}</span></div>
            )}
            <div style={s.grandTotalRow}>
              <span style={s.grandTotalLabel}>Selected Contract Total</span>
              <span style={s.grandTotalVal}>${selectedContractTotalWithTax.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Session 3.4 — Approval Lock */}
        <div style={s.approvalBox}>
          {isLocked ? (
            <>
              <div style={{ ...s.blockLabel, color: proposal.status === 'approved' ? '#16a34a' : '#dc2626' }}>
                {proposal.status === 'approved' ? '✓ Approved' : '✗ Declined'}
              </div>
              <div style={s.approvalMeta}>
                {proposal.status === 'approved'
                  ? `Locked at $${(proposal.approved_contract_amount || 0).toFixed(2)} on ${new Date(proposal.approved_at).toLocaleDateString()} (${proposal.approval_source}). Optional add-ons can no longer be changed on this proposal.`
                  : `Declined on ${new Date(proposal.declined_at).toLocaleDateString()} (${proposal.approval_source}).`}
              </div>
            </>
          ) : (
            <>
              <div style={s.blockLabel}>Record Customer Approval</div>
              <div style={s.approvalMeta}>No client portal yet — record what the customer told you.</div>
              <div style={s.approvalActions}>
                <select style={s.approvalSelect} value={approvalSource} onChange={e => setApprovalSource(e.target.value)}>
                  <option value="manual">Manual (text/phone/in person)</option>
                  <option value="email">Email</option>
                </select>
                <button
                  style={s.optionAcceptBtn}
                  disabled={approving}
                  onClick={() => handleApprove(approvalSource)}
                >
                  {approving ? '...' : 'Mark Approved'}
                </button>
                <button
                  style={s.optionDeclineBtn}
                  disabled={approving}
                  onClick={() => handleDecline(approvalSource)}
                >
                  {approving ? '...' : 'Mark Declined'}
                </button>
              </div>
            </>
          )}
        </div>

        {visibility.show_deposit_info !== false && (
          <div style={s.paymentSummary}>
            <div style={s.blockLabel}>Payment Summary</div>
            <div style={s.paymentRow}><span style={s.paymentLabel}>Selected Contract Total</span><span style={s.paymentVal}>${selectedContractTotalWithTax.toFixed(2)}</span></div>
            <div style={s.paymentRow}><span style={s.paymentLabel}>Deposit to Begin Work ({proposal.deposit_pct || 0}%)</span><span style={{ ...s.paymentVal, color: '#2d6a2d', fontWeight: '700' }}>${(proposal.deposit_amount || 0).toFixed(2)}</span></div>
            <div style={s.paymentRow}><span style={s.paymentLabel}>Remaining Balance Due on Completion</span><span style={s.paymentVal}>${(selectedContractTotalWithTax - (proposal.deposit_amount || 0)).toFixed(2)}</span></div>
          </div>
        )}

        {visibility.show_client_notes !== false && clientNotes && (
          <div style={s.notesBlock}>
            <div style={s.blockLabel}>Notes</div>
            <div style={s.blockText}>{clientNotes}</div>
          </div>
        )}

        {visibility.show_payment_terms !== false && (
          <div style={s.notesBlock}>
            <div style={s.blockLabel}>Payment Terms</div>
            <div style={{ ...s.blockText, color: '#6b7c6b', fontSize: '12px' }}>{proposal.payment_terms_text || DEFAULT_PAYMENT_TERMS}</div>
          </div>
        )}

        <div style={s.footer}>
          {visibility.show_expiry_date !== false && proposal.expires_at
            ? `This proposal is valid until ${new Date(proposal.expires_at + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. `
            : 'This proposal is valid for 30 days from the date above. '}
          TLC Unlimited · El Paso, TX
        </div>
      </div>
    </div>
  )
}

const s = {
  page: { width: '100%', paddingBottom: '60px' },
  loading: { padding: '60px', textAlign: 'center', color: '#6b7c6b' },
  header: { marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' },
  convertBtn: { background: '#2d6a2d', color: 'white', border: 'none', padding: '11px 20px', borderRadius: '9px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 },
  backLink: { display: 'inline-block', color: '#6b7c6b', textDecoration: 'none', fontSize: '13px', marginBottom: '8px' },
  titleRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { margin: 0, fontSize: '22px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '13px' },
  statusBadge: { padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize' },
  cautionBanner: { display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#78350f', lineHeight: '1.5' },
  doc: { background: 'white', borderRadius: '14px', padding: '36px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
  docHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '24px', borderBottom: '2px solid #f3f4f6' },
  proposalTag: { display: 'inline-block', background: '#1a2e1a', color: 'white', fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', padding: '4px 10px', borderRadius: '4px', marginBottom: '8px' },
  companyName: { fontSize: '22px', fontWeight: '700', color: '#1a2e1a' },
  companyInfo: { fontSize: '13px', color: '#6b7c6b', marginTop: '2px' },
  docHeaderRight: { textAlign: 'right' },
  proposalNumLarge: { fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  proposalDate: { fontSize: '13px', color: '#6b7c6b', marginTop: '4px' },
  proposalExpiry: { fontSize: '13px', color: '#6b7c6b', marginTop: '2px' },
  billTo: { marginBottom: '24px', padding: '16px', background: '#f9fafb', borderRadius: '8px' },
  billToLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' },
  billToName: { fontWeight: '700', fontSize: '16px', color: '#1a2e1a', marginBottom: '4px' },
  billToInfo: { fontSize: '13px', color: '#6b7c6b', margin: '2px 0' },
  blockLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' },
  blockText: { fontSize: '13px', color: '#374151', lineHeight: '1.6' },
  scopeBlock: { background: '#f8faf8', borderLeft: '3px solid #2d4a2d', padding: '12px 16px', marginBottom: '24px', borderRadius: '0 6px 6px 0' },
  sectionsBlock: { marginBottom: '20px' },
  sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 14px', borderBottom: '1px solid #f3f4f6', gap: '12px' },
  sectionName: { fontWeight: '600', fontSize: '14px', color: '#1a2e1a' },
  sectionDesc: { fontSize: '12px', color: '#6b7c6b', marginTop: '3px', lineHeight: '1.5' },
  sectionTotal: { fontWeight: '700', fontSize: '15px', color: '#1a2e1a', whiteSpace: 'nowrap' },
  emptyNote: { padding: '16px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' },
  optionsBlock: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px' },
  optionBadge: { fontSize: '11px', fontWeight: '600', padding: '2px 9px', borderRadius: '10px', whiteSpace: 'nowrap' },
  optionActions: { display: 'flex', gap: '6px' },
  optionAcceptBtn: { background: '#16a34a', color: 'white', border: 'none', padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  optionDeclineBtn: { background: 'white', color: '#dc2626', border: '1.5px solid #fecaca', padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  optionResetBtn: { background: 'none', color: '#6b7280', border: '1.5px solid #e5e7eb', padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  optionErrorBox: { background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' },
  approvalBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px 18px', marginBottom: '20px' },
  approvalMeta: { fontSize: '13px', color: '#6b7c6b', lineHeight: 1.5, marginBottom: '10px' },
  approvalActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  approvalSelect: { padding: '7px 10px', borderRadius: '7px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit' },
  totalsWrap: { display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' },
  totalsBox: { width: '340px' },
  totalsRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: '#374151' },
  totalsLabel: { color: '#6b7c6b' },
  totalsVal: { fontWeight: '500' },
  grandTotalRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: '4px' },
  grandTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#1a2e1a' },
  grandTotalVal: { fontSize: '22px', fontWeight: '800', color: '#2d6a2d' },
  paymentSummary: { background: '#f8faf8', border: '1px solid #d1e7d1', borderRadius: '8px', padding: '18px 20px', marginBottom: '20px' },
  paymentRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e5e7eb' },
  paymentLabel: { color: '#555' },
  paymentVal: { fontWeight: '600' },
  notesBlock: { marginBottom: '20px' },
  footer: { borderTop: '1px solid #e5e7eb', paddingTop: '16px', fontSize: '11px', color: '#9ca3af', lineHeight: '1.6' },
}
